import { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  IconButton,
  Stack,
  Typography
} from "@material-ui/core";
import { HelpOutline as HelpOutlineIcon } from "@material-ui/icons";
import { CoinBalanceBreakdown } from "../../../@types/sharedSchema";
import BuyTokensModal from "./BuyTokensModal";
import TokensExplainerModal from "./TokensExplainerModal";

interface CoinBalanceDisplayProps {
  balance: CoinBalanceBreakdown | null;
  loading?: boolean;
}

/**
 * Compact token-balance card shown on the creatives page. Shows the total balance,
 * any monthly tokens about to expire, and an always-available "buy more" button
 * that opens the token-packs modal.
 */
export default function CoinBalanceDisplay({ balance, loading }: CoinBalanceDisplayProps): JSX.Element {
  const { t } = useTranslation();
  const [buyOpen, setBuyOpen] = useState(false);
  const [explainerOpen, setExplainerOpen] = useState(false);

  const formatDate = (d: Date | null) =>
    d
      ? new Date(d).toLocaleDateString("he-IL", { day: "numeric", month: "short", year: "numeric" })
      : "";

  return (
    <Card sx={{ mb: 3 }}>
      <CardContent>
        <Stack direction="row" alignItems="center" spacing={1.5} flexWrap="wrap">
          <Box component="span" sx={{ fontSize: 28, lineHeight: 1 }} aria-hidden>
            🪙
          </Box>
          <Box sx={{ mr: 2 }}>
            <Stack direction="row" alignItems="center" spacing={0.5}>
              <Typography variant="subtitle2" color="text.secondary">
                {t("creativesGenerator.coins.balanceTitle", "יתרת טוקנים")}
              </Typography>
              <IconButton
                size="small"
                onClick={() => setExplainerOpen(true)}
                aria-label={t("creativesGenerator.coins.explainer.title", "מה הם טוקנים?")}
                sx={{ color: "text.secondary" }}
              >
                <HelpOutlineIcon fontSize="inherit" />
              </IconButton>
            </Stack>
            {loading ? (
              <CircularProgress size={20} sx={{ mt: 0.5 }} />
            ) : (
              <Typography variant="h5">{balance?.total ?? 0}</Typography>
            )}
          </Box>
          {balance && balance.monthly > 0 && balance.monthlyExpiresAt && (
            <Chip
              size="small"
              label={t("creativesGenerator.coins.monthlyExpiry", {
                defaultValue: "{{count}} טוקנים חודשיים פגים בתאריך {{date}}",
                count: balance.monthly,
                date: formatDate(balance.monthlyExpiresAt)
              })}
            />
          )}
          <Box sx={{ flexGrow: 1 }} />
          <Button variant="outlined" size="small" onClick={() => setBuyOpen(true)}>
            {t("creativesGenerator.coins.buyMore", "לרכישת טוקנים")}
          </Button>
        </Stack>
      </CardContent>

      <BuyTokensModal open={buyOpen} onClose={() => setBuyOpen(false)} />
      <TokensExplainerModal open={explainerOpen} onClose={() => setExplainerOpen(false)} />
    </Card>
  );
}
